/**
 * Present mode: one report page at a time, full screen, sized to the page's
 * own shape. Arrow keys or the on-screen buttons move between pages; Escape
 * leaves. Uses the browser's full screen when allowed, and works without it.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

export function Present({
  count,
  ratio,
  start,
  renderPage,
  onClose,
}: {
  count: number;
  ratio: number;
  start: number;
  renderPage: (index: number, width: number) => ReactNode;
  onClose: () => void;
}): ReactNode {
  const [index, setIndex] = useState(start);
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const box = useRef<HTMLDivElement | null>(null);

  // The editor behind re-renders while presenting (saves, queries), handing
  // over a new onClose each time. Keep the latest in a ref so the set-up below
  // runs once: re-running it would leave and re-enter full screen, and that
  // exit would read as "close" and drop the viewer back to the start.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    let entered = false;
    box.current
      ?.requestFullscreen?.()
      .then(() => {
        entered = true;
      })
      .catch(() => undefined);
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    // Only a full screen we entered and the viewer then left (Esc) closes the presentation.
    const onFullscreen = () => {
      if (entered && !document.fullscreenElement) close.current();
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('fullscreenchange', onFullscreen);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
      if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(event.key)) {
        event.preventDefault();
        setIndex((at) => Math.min(count - 1, at + 1));
      }
      if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
        event.preventDefault();
        setIndex((at) => Math.max(0, at - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count]);

  // The largest page of this shape that fits the screen, with room for the controls.
  const width = Math.min(size.width - 48, (size.height - 96) / ratio);
  return (
    <div
      ref={box}
      className="rpr"
      role="dialog"
      aria-modal="true"
      aria-label={`Presenting page ${index + 1} of ${count}`}
    >
      <div className="rpr-page" style={{ width, height: width * ratio }}>
        {renderPage(index, width)}
      </div>
      <div className="rpr-bar">
        <button
          type="button"
          className="rb-icon"
          aria-label="Previous page"
          disabled={index === 0}
          onClick={() => setIndex(index - 1)}
        >
          ‹
        </button>
        <span className="mono" aria-live="polite">
          {index + 1} / {count}
        </span>
        <button
          type="button"
          className="rb-icon"
          aria-label="Next page"
          disabled={index === count - 1}
          onClick={() => setIndex(index + 1)}
        >
          ›
        </button>
        <button type="button" className="btn small secondary" onClick={onClose}>
          Close (Esc)
        </button>
      </div>
    </div>
  );
}
